import React, { useState, useMemo, useRef } from "react";
import ReactApexChart from "react-apexcharts";
import ChartBurgerMenu from "./ChartBurgerMenu";
import FieldHelp from "../help/FieldHelp";
import AIAnalyzerModal from "./AIAnalyzerModal";
import AIAnalyzerButton from "./AIAnalyzerButton";
import { buildChartPayload } from "../utils/aiAnalyzerEngine";
import {
  formatAmount,
  resolveTariffs,
  unitLabel,
} from "../utils/currencyConversion";

// Helper function to safely parse dates across different formats and prevent timezone shifts
const parseDateSafe = (dateStr) => {
  if (!dateStr) return NaN;
  if (dateStr instanceof Date) return dateStr.getTime();
  if (typeof dateStr !== "string") return new Date(dateStr).getTime();

  // Parse YYYY-MM-DD HH:mm:ss format as naive local time to avoid timezone shifts
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}):(\d{2}))?/);
  if (match) {
    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1;
    const day = parseInt(match[3], 10);
    const hour = parseInt(match[4] || "0", 10);
    const minute = parseInt(match[5] || "0", 10);
    const second = parseInt(match[6] || "0", 10);
    return new Date(year, month, day, hour, minute, second).getTime();
  }
  return new Date(dateStr).getTime();
};

const SolarProductionComparison = ({
  data = [],
  dateRange,
  currencyMode = "Energy",
  tariffs,
}) => {
  const chartRef = useRef(null);
  // Generated solar units are valued at the client's solar rate (falling back
  // to the grid rate when unset - see resolveTariffs).
  const isPkr = currencyMode === "PKR";
  const rates = useMemo(() => tariffs || resolveTariffs(null), [tariffs]);
  const valueUnit = unitLabel(isPkr);
  const solarRate = isPkr ? rates.solar : 1;

  // Generate unique IDs for this instance to avoid ApexCharts ID collisions
  const instanceId = useMemo(
    () => `solar-prod-${Math.random().toString(36).substring(2, 9)}`,
    [],
  );
  const mainChartId = `${instanceId}-main`;
  const brushChartId = `${instanceId}-brush`;

  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiModalPayload, setAiModalPayload] = useState(null);

  const handleAnalyzeSolarComparison = () => {
    const labels = (data || []).map((d) => {
      const ts = parseDateSafe(d.timestamp);
      return Number.isNaN(ts) ? String(d.timestamp) : new Date(ts).toLocaleString();
    });
    const datasets = [
      { label: "Ideal Production", data: (expectedData || []).map((d) => d[1]) },
      { label: "Actual Production", data: (actualData || []).map((d) => d[1]) },
    ];
    const payload = buildChartPayload({
      chartTitle: isPkr
        ? "Solar Energy Value: Ideal vs Actual"
        : "Solar Energy Production: Ideal vs Actual",
      metricName: isPkr ? "Solar Energy Value" : "Solar Energy Production",
      unit: valueUnit,
      timestamps: labels,
      datasets: datasets,
      dateRange: dateRange,
    });
    setAiModalPayload(payload);
    setAiModalOpen(true);
  };

  const parseNumericOrNull = (val) => {
    if (val === null || val === undefined) return null;
    if (typeof val === "string" && val.trim() === "") return null;
    const n = Number(val);
    return Number.isFinite(n) ? n : null;
  };

  const alignedChartData = useMemo(() => {
    if (!Array.isArray(data)) return { expected: [], actual: [] };

    const mapped = data
      .map((item) => {
        const ts = parseDateSafe(item.timestamp);
        const expectedKwh = parseNumericOrNull(item.expected_generation_kwh);
        const actualKwh = parseNumericOrNull(item.actual_generation_kwh);

        if (!Number.isFinite(ts)) return null;
        if (expectedKwh === null && actualKwh === null) return null;

        // Null stays null so the chart keeps drawing a gap instead of a 0.
        const expected = expectedKwh === null ? null : expectedKwh * solarRate;
        const actual = actualKwh === null ? null : actualKwh * solarRate;

        return { ts, expected, actual };
      })
      .filter((pt) => pt !== null)
      .sort((a, b) => a.ts - b.ts);

    const deduped = [];
    for (const pt of mapped) {
      if (deduped.length > 0 && pt.ts === deduped[deduped.length - 1].ts) {
        deduped[deduped.length - 1] = pt;
      } else {
        deduped.push(pt);
      }
    }

    return {
      expected: deduped.map((pt) => [pt.ts, pt.expected]),
      actual: deduped.map((pt) => [pt.ts, pt.actual]),
    };
  }, [data, solarRate]);

  const expectedData = alignedChartData.expected;
  const actualData = alignedChartData.actual;

  const series = useMemo(
    () => [
      {
        name: "Ideal Production",
        data: expectedData,
      },
      {
        name: "Actual Production",
        data: actualData,
      },
    ],
    [expectedData, actualData],
  );

  const yMax = useMemo(() => {
    const allVals = [...expectedData, ...actualData]
      .map((pt) => pt[1])
      .filter((val) => Number.isFinite(val));
    if (!allVals || allVals.length === 0) return undefined;
    const maxVal = Math.max(...allVals);
    if (!Number.isFinite(maxVal) || maxVal <= 0) return undefined;

    const headroom = Math.max(maxVal * 0.03, 0.5);
    return Number((maxVal + headroom).toFixed(2));
  }, [expectedData, actualData]);

  const chartOptions = useMemo(
    () => ({
      chart: {
        id: mainChartId,
        type: "line",
        height: 350,
        zoom: { enabled: false },
        toolbar: { show: false },
      },
      colors: ["#059669", "#ca8a04"],
      dataLabels: { enabled: false },
      stroke: { curve: "smooth", width: 3.5, connectNullData: false },
      legend: {
        position: "bottom",
        horizontalAlign: "center",
        itemMargin: { horizontal: 10, vertical: 5 },
        markers: { radius: 12 },
      },
      xaxis: {
        type: "datetime",
        min: dateRange?.start ? parseDateSafe(dateRange.start) : undefined,
        max: dateRange?.end ? parseDateSafe(dateRange.end) : undefined,
        tooltip: { enabled: true, format: "HH:mm" },
        axisBorder: { show: false },
        axisTicks: { show: true },
        tickAmount: 6,
        labels: {
          datetimeUTC: false,
          datetimeFormatter: {
            year: "yyyy",
            month: "MMM 'yy",
            day: "dd MMM",
            hour: "HH:mm",
          },
        },
      },
      yaxis: {
        title: {
          text: `Solar Production (${valueUnit})`,
          style: { color: "#64748b", fontWeight: 500 },
        },
        labels: {
          // PKR totals are 1-2 orders of magnitude larger than the kWh they
          // come from, so drop the cents and group thousands to keep the axis
          // narrow; kWh keeps its two decimals.
          formatter: (val) =>
            typeof val === "number" ? formatAmount(val, isPkr ? 0 : 2) : val,
        },
        min: 0,
        max: typeof yMax === "number" ? yMax : undefined,
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: (val) =>
            typeof val === "number" ? `${formatAmount(val)} ${valueUnit}` : "–",
        },
        custom: function ({ series, dataPointIndex, w }) {
          const colors = w.config.colors || ["#059669", "#ca8a04"];
          const expected = series && series[0] ? series[0][dataPointIndex] : null;
          const actual = series && series[1] ? series[1][dataPointIndex] : null;
          const variance =
            typeof actual === "number" && actual > 0 && typeof expected === "number"
              ? (actual / expected) * 100
              : null;

          const fmt = (v) =>
            typeof v === "number" ? `${formatAmount(v)} ${valueUnit}` : "–";
          const fmtPct = (v) => (typeof v === "number" ? `${v.toFixed(2)}%` : "–");

          let xVal = null;
          try {
            if (w && w.globals && Array.isArray(w.globals.seriesX)) {
              const px = w.globals.seriesX[0];
              xVal = Array.isArray(px) ? px[dataPointIndex] : px;
            }
          } catch (e) {
            xVal = null;
          }
          if (xVal == null && w && w.globals && Array.isArray(w.globals.labels)) {
            xVal = w.globals.labels[dataPointIndex];
          }

          const timeLabel = xVal ? new Date(xVal).toLocaleString() : "";

          return (
            '<div style="padding:10px;min-width:190px;font-size:13px;color:#0f172a;background:#fff;border:1px solid rgba(15,23,42,0.06);border-radius:8px">' +
            `<div style="font-weight:700;margin-bottom:8px">${timeLabel}</div>` +
            `<div style="display:flex;align-items:center;gap:8px;margin-bottom:6px"><span style="width:10px;height:10px;border-radius:50%;background:${colors[0]}"></span><div>Ideal Production: <strong>${fmt(expected)}</strong></div></div>` +
            `<div style="display:flex;align-items:center;gap:8px;margin-bottom:6px"><span style="width:10px;height:10px;border-radius:50%;background:${colors[1]}"></span><div>Actual Production: <strong>${fmt(actual)}</strong></div></div>` +
            `<div style="display:flex;align-items:center;gap:8px"><div style="width:10px"></div><div>Productivity: <strong>${fmtPct(variance)}</strong></div></div>` +
            "</div>"
          );
        },
      },
      grid: {
        borderColor: "#f1f5f9",
        strokeDashArray: 0,
        yaxis: { lines: { show: true } },
      },
    }),
    [mainChartId, yMax, dateRange, valueUnit, isPkr],
  );

  const dataRangeTimestamps = useMemo(() => {
    const allTs = [...expectedData, ...actualData].map((pt) => pt[0]);
    if (allTs.length === 0) return null;
    return { min: Math.min(...allTs), max: Math.max(...allTs) };
  }, [expectedData, actualData]);

  const hasData = useMemo(() => {
    const expectedHasFinite = expectedData.some((pt) => Number.isFinite(pt[1]));
    const actualHasFinite = actualData.some((pt) => Number.isFinite(pt[1]));
    return expectedHasFinite || actualHasFinite;
  }, [expectedData, actualData]);

  const selectedRangeData = useMemo(() => {
    const startTs = dateRange?.start ? parseDateSafe(dateRange.start) : null;
    const endTs = dateRange?.end ? parseDateSafe(dateRange.end) : null;

    const isValidRange =
      Number.isFinite(startTs) && Number.isFinite(endTs) && startTs <= endTs;

    const withinRange = (timestamp) => {
      if (!isValidRange) return true;
      return timestamp >= startTs && timestamp <= endTs;
    };

    const sumSeriesFromRaw = (rawData, key) => {
      if (!Array.isArray(rawData)) return 0;

      return rawData.reduce((sum, item) => {
        const ts = parseDateSafe(item.timestamp);
        if (!Number.isFinite(ts) || !withinRange(ts)) return sum;

        const rawVal = item[key];
        if (rawVal === null || rawVal === undefined) return sum;
        if (typeof rawVal === "string" && rawVal.trim() === "") return sum;

        const numericVal = Number(rawVal);
        return Number.isFinite(numericVal) ? sum + numericVal : sum;
      }, 0);
    };

    const expectedTotal =
      sumSeriesFromRaw(data, "expected_generation_kwh") * solarRate;
    const actualTotal =
      sumSeriesFromRaw(data, "actual_generation_kwh") * solarRate;
    // Both totals carry the same rate, so the ratio is rate-independent and
    // Productivity stays a percentage in either mode.
    const variancePercent =
      actualTotal > 0 ? (actualTotal / expectedTotal) * 100 : null;

    return {
      expectedTotal,
      actualTotal,
      variancePercent,
      hasActiveRange: isValidRange,
    };
  }, [data, dateRange?.end, dateRange?.start, solarRate]);

  const formatEnergy = (value) => `${formatAmount(value)} ${valueUnit}`;
  const formatPercent = (value) =>
    Number.isFinite(value) ? `${value.toFixed(2)}%` : "–";

  return (
    <div
      style={{
        marginTop: 24,
        padding: "20px",
        background: "#fff",
        borderRadius: "12px",
        border: "1px solid #e2e8f0",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <div style={{ display: "flex", gap: "12px" }}>
          <h3
            style={{
              fontSize: "16px",
              fontWeight: 600,
              color: "#0f172a",
              margin: 0,
            }}
          >
            <FieldHelp id="historical.solarIdealVsActual">
              Solar Energy Production: Ideal vs Actual
            </FieldHelp>
          </h3>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <AIAnalyzerButton onClick={handleAnalyzeSolarComparison} />
          <ChartBurgerMenu
            chartRef={chartRef}
            chartTitle={
              isPkr
                ? "Solar_Production_Value_Comparison"
                : "Solar_Production_Comparison"
            }
            onAIAnalyze={handleAnalyzeSolarComparison}
            exportContainer={true}
            chartData={{
              labels: data.map((d) => {
                const ts = parseDateSafe(d.timestamp);
                return Number.isNaN(ts) ? "–" : new Date(ts).toLocaleString();
              }),
              series: [
                expectedData.map((d) => d[1]),
                actualData.map((d) => d[1]),
              ],
              seriesNames: ["Ideal Production", "Actual Production"],
              unit: valueUnit,
            }}
            chartType="solar_comparison"
          />
        </div>
      </div>

      {hasData && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "12px",
            marginBottom: "18px",
          }}
        >
          <div
            style={{
              padding: "14px 16px",
              borderRadius: "12px",
              background: "#fef9c3",
              border: "1px solid #fde047",
              boxShadow: "0 6px 16px rgba(0,0,0,0.10)",
            }}
          >
            <div style={{ fontSize: "12px", color: "#ca8a04", fontWeight: 600 }}>
              <FieldHelp id="historical.actualSolar">
                Actual Solar Energy {isPkr ? "Value" : "Production"}
              </FieldHelp>
            </div>
            <div style={{ marginTop: "8px", fontSize: "22px", fontWeight: 700, color: "#ca8a04" }}>
              {formatEnergy(selectedRangeData.actualTotal)}
            </div>
          </div>

          <div
            style={{
              padding: "14px 16px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, rgba(5,150,105,0.08) 0%, #ecfdf5 100%)",
              border: "1px solid #059669",
              boxShadow: "0 6px 16px rgba(0,0,0,0.10)",
            }}
          >
            <div style={{ fontSize: "12px", color: "#059669", fontWeight: 600 }}>
              <FieldHelp id="historical.idealSolar">
                Ideal Solar Energy {isPkr ? "Value" : "Production"}
              </FieldHelp>
            </div>
            <div style={{ marginTop: "8px", fontSize: "22px", fontWeight: 700, color: "#059669" }}>
              {formatEnergy(selectedRangeData.expectedTotal)}
            </div>
          </div>

          <div
            style={{
              padding: "14px 16px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%)",
              border: "1px solid #fb7185",
              boxShadow: "0 6px 16px rgba(0,0,0,0.10)",
            }}
          >
            <div style={{ fontSize: "12px", color: "#e11d48", fontWeight: 600 }}>
              <FieldHelp id="historical.solarProductivity">Productivity</FieldHelp>
            </div>
            <div style={{ marginTop: "8px", fontSize: "22px", fontWeight: 700, color: "#be123c" }}>
              {formatPercent(selectedRangeData.variancePercent)}
            </div>
          </div>
        </div>
      )}

      <div
        id="chart-wrapper"
        ref={chartRef}
        style={{
          background: "#fff",
          padding: "10px 0",
          width: "100%",
          boxSizing: "border-box",
          minHeight: hasData ? "auto" : "200px",
        }}
      >
        {!hasData ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "200px",
              color: "#64748b",
            }}
          >
            No solar production data available for this period
          </div>
        ) : (
          <ReactApexChart
            key={mainChartId}
            options={chartOptions}
            series={series}
            type="line"
            height={420}
            width="100%"
          />
        )}
      </div>
      <AIAnalyzerModal
        isOpen={aiModalOpen}
        onClose={() => setAiModalOpen(false)}
        chartPayload={aiModalPayload}
      />
    </div>
  );
};

export default SolarProductionComparison;


